import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider as DescopeAuthProvider } from '@descope/react-sdk';

import { BootstrapSkeleton } from './shared/components/BootstrapSkeleton';
import { resolveUiEntry } from './core/router/ui-entry';
import { config } from './core/config/env';
import { AuthProvider } from './core/contexts/AuthContext';
import { TenantProvider } from './core/contexts/TenantContext';
import { LOOKUP_ERROR_EVENT, LookupReadError } from './core/services/crud.service';

if (!config.descopeProjectId) {
  throw new Error(
    'VITE_DESCOPE_PROJECT_ID is missing. Copy app-backoffice/.env-example to .env and restart Vite.',
  );
}

// Vite injects BASE_URL based on the `base` config option. Stripping the
// trailing slash gives React Router its `basename` so client routes work
// both at `/` (dev) and `/backoffice` (production behind the backend).
const entry = resolveUiEntry(window.location.pathname, import.meta.env.BASE_URL, window.location);
const LegacyApp = lazy(() => import('./oldui/App'));
const NewApp = lazy(() => import('./app/App'));

function Interface() {
  return entry.legacy ? <LegacyApp /> : <NewApp />;
}

if (entry.redirectHref) {
  window.location.replace(entry.redirectHref);
} else {
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        if (error instanceof LookupReadError) {
          window.dispatchEvent(new CustomEvent(LOOKUP_ERROR_EVENT, { detail: error.message }));
        }
      },
    }),
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
    },
  });

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter basename={entry.basename}>
            <DescopeAuthProvider projectId={config.descopeProjectId}>
              <AuthProvider>
                <TenantProvider>
                  <Suspense fallback={<BootstrapSkeleton/>}>
                    <Interface />
                  </Suspense>
                </TenantProvider>
              </AuthProvider>
            </DescopeAuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
    </React.StrictMode>,
  );
}
